// Weight logs API hooks. Mirrors useNutritionLogs.ts — one root cache key,
// list + create paths invalidate the same key on mutation. The create
// mutation also invalidates ['reviews'] so the weekly review screen picks
// up new logs without a manual refetch (per ADR-0017's compute-on-demand
// review endpoint).
import type {
  CreateWeightLogRequest,
  UpdateWeightLogRequest,
  WeightLog,
  WeightLogListResponse,
} from '@onflow/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';

const WEIGHT_LOGS_KEY = ['weight-logs'] as const;
const REVIEWS_KEY = ['reviews'] as const;

interface ListWeightLogsArgs {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
}

export function useWeightLogsQuery(args: ListWeightLogsArgs = {}) {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 20;
  return useQuery<WeightLogListResponse>({
    queryKey: [...WEIGHT_LOGS_KEY, 'list', page, pageSize, args.from ?? null, args.to ?? null],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (args.from) params.set('from', args.from);
      if (args.to) params.set('to', args.to);
      return api<WeightLogListResponse>(`/logs/weight?${params.toString()}`);
    },
    staleTime: 30_000,
  });
}

export function useCreateWeightLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWeightLogRequest) =>
      api<WeightLog>('/logs/weight', { method: 'POST', json: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEIGHT_LOGS_KEY });
      qc.invalidateQueries({ queryKey: REVIEWS_KEY });
    },
  });
}

function invalidateWeightEvidence(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: WEIGHT_LOGS_KEY });
  qc.invalidateQueries({ queryKey: REVIEWS_KEY });
}

export function useUpdateWeightLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateWeightLogRequest }) =>
      api<WeightLog>(`/logs/weight/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => invalidateWeightEvidence(qc),
  });
}

export function useDeleteWeightLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/logs/weight/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateWeightEvidence(qc),
  });
}
